import { useLayoutEffect, useRef, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import type { UsageWindowDays } from './types';

const OPTIONS: { value: UsageWindowDays; label: string }[] = [
  { value: 7, label: '7 days' },
  { value: 15, label: '15 days' },
  { value: 30, label: '30 days' },
];

export interface TimeRangeToggleProps {
  value: UsageWindowDays;
  onChange: (value: UsageWindowDays) => void;
}

export function TimeRangeToggle({ value, onChange }: TimeRangeToggleProps) {
  const index = OPTIONS.findIndex((option) => option.value === value);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [metrics, setMetrics] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const el = buttonRefs.current[index];
      if (el) setMetrics({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [index]);

  const pillSpring = useSpring({
    left: metrics.left,
    width: metrics.width,
    config: { tension: 400, friction: 30 },
  });

  return (
    <div className="relative inline-flex rounded-full bg-muted p-0.5">
      <animated.span
        className="absolute bottom-0.5 top-0.5 rounded-full bg-card shadow-sm"
        style={{ left: pillSpring.left, width: pillSpring.width }}
        aria-hidden
      />
      {OPTIONS.map((option, i) => (
        <button
          key={option.value}
          ref={(el) => {
            buttonRefs.current[i] = el;
          }}
          type="button"
          onClick={() => onChange(option.value)}
          className={[
            'relative z-10 whitespace-nowrap rounded-full px-3.5 py-1.5 text-center text-xs font-medium transition-colors duration-150',
            value === option.value ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
